import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import React, { FC, memo } from 'react';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import Icon from '~ComponentsImproved/Icon';
import ArrowForwardIcon from '~Assets/icons/arrow_forward.svg';
import PhoneIcon from '~Assets/icons/phone_android.svg';
import { useButton, useCurrency, useI18n } from '~Hooks';
import { AdvancedSearchOrders_operativeViewer_advancedSearchOrders_orders } from './__queries__';
import { Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useStorage } from '~Components/Storage';

const OrderRowContainer = styled.div<{ isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: 16px;
  text-transform: none;
  margin-bottom: 8px;
  min-height: 64px;
  box-sizing: border-box;
  align-items: center;
  position: relative;
  overflow: hidden;

  &:active {
    background-color: var(--colorBlueGray900);
  }

  border: ${({ isSelected }) =>
    isSelected ? '1px solid var(--colorBlueGray700)' : '1px solid var(--colorBlueGray1100)'};
`;

const Column = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 96px;
  min-width: 96px;
  flex-shrink: 0;
`;

const OrderColor = styled.div<{ color: string }>`
  position: absolute;
  left: 0;
  height: 100%;
  top: 0;
  width: 4px;
  background-color: ${({ color }) => color};
`;

const ExpressTag = styled.span`
  border: 1px solid var(--colorWhite);
  display: inline-block;
  margin-right: 2px;
  padding: 2px 4px;
  border-radius: 2px;
  font-size: 10px;
  margin-top: 1px;
  color: var(--colorWhite);
`;

/**
 * Get the color indicator for an order based on its status
 */
const getOrderColor = (status: ORDER_STATUS) => {
  switch (status) {
    case ORDER_STATUS.COMPLETED:
      return 'var(--colorUIGreen)';
    case ORDER_STATUS.REFUNDED:
      return 'var(--colorUIRed)';
    case ORDER_STATUS.ACCEPTED:
      return 'var(--colorUIYellow)';
    default:
      return 'var(--colorBlueGray1300)';
  }
};

interface OrderRowProps {
  order: AdvancedSearchOrders_operativeViewer_advancedSearchOrders_orders;
  isSelected: boolean;
  onOrderClick: (orderId: string) => void;
}

/**
 * Individual order row component. Selection is passed in as a prop (instead of
 * read from the URL here) so a selection change only re-renders the affected rows.
 */
const OrderRow: FC<OrderRowProps> = ({ order, isSelected, onOrderClick }) => {
  const { i18n } = useI18n();
  const currency = useCurrency();
  const [storage] = useStorage();

  // Handle order click - update URL with orderId parameter
  const orderButton = useButton({
    onClick: () => onOrderClick(order.id),
  });

  return (
    <OrderRowContainer {...orderButton} isSelected={isSelected}>
      <OrderColor color={getOrderColor(order.status)} />

      <Box display="flex" alignItems="center" gap="xs">
        {order.origin === 'APP' && (
          <Box display="flex" alignItems="center">
            <Icon src={PhoneIcon} color="var(--colorWhite)" height="20px" />
          </Box>
        )}
        {order.origin === 'EXPRESS' && (
          <Box display="flex" alignItems="center">
            <ExpressTag>EXPRESS</ExpressTag>
          </Box>
        )}
        <Column>
          <Heading level={7} color="var(--colorBlueGray200)" bottom={0}>
            {i18n('History.Order')}
          </Heading>
          <Paragraph fontWeight={600} level={4} color="var(--colorWhite)">
            {'#' + order.easyId}
          </Paragraph>
        </Column>
      </Box>

      <Column>
        <Heading level={7} color="var(--colorBlueGray200)" bottom={0}>
          {i18n('History.Time')}
        </Heading>
        <Paragraph fontWeight={600} level={4} color="var(--colorWhite)">
          {DateTime.fromISO(order.createdAt!).toFormat('HH:mm')}
        </Paragraph>
      </Column>

      <Column>
        <Heading level={7} color="var(--colorBlueGray200)" bottom={0}>
          {i18n('POS.Total')}
        </Heading>
        <Paragraph fontWeight={600} level={4} color="var(--colorWhite)">
          {currency.format(order.payment?.amount ?? 0)}
        </Paragraph>
      </Column>

      {storage.enableTipping && window.innerWidth > 1380 && (
        <Column>
          <Heading level={7} color="var(--colorBlueGray200)" bottom={0}>
            {i18n('History.Tip')}
          </Heading>
          <Paragraph fontWeight={600} level={4} color="var(--colorWhite)">
            {order.payment.tip ? currency.format(order.payment.tip) : '--'}
          </Paragraph>
        </Column>
      )}

      <Box display="flex" alignItems="center">
        <Icon
          src={ArrowForwardIcon}
          color={isSelected ? 'var(--colorWhite)' : 'var(--colorBlueGray1100)'}
        />
      </Box>
    </OrderRowContainer>
  );
};

export default memo(OrderRow);
